<script setup lang="ts">
import EmptyState from '@/components/assistant/chat/EmptyState.vue'
import InputArea from '@/components/assistant/chat/InputArea.vue'
import NonEmptyState from '@/components/assistant/chat/NonEmptyState.vue'
import UsageLimitMessage from '@/components/assistant/chat/UsageLimitMessage.vue'
import type {ConversationIntent} from '@/components/assistant/types'
import type {CreditUsage} from '@/client/mentor/CreditUsageClient'
import type {UserMessageContent} from '@/client/mentor/MessagesClient'
import type {UserPrompt} from '@/components/assistant/chat/InputArea.vue'
import {AssistantMessageStatus, ConversationMessageType} from '@/client/mentor/MessagesClient'
import {CHAT_AREA_INPUT_MAX_WIDTH_PX, CHAT_AREA_MESSAGES_MAX_WIDTH_PX} from '@/components/assistant/constants'
import {computed} from 'vue'
import {storeToRefs} from 'pinia'
import {useAssistantMessages} from '@/composables/query/mentor/useAssistantMessages'
import {useAssistantSendMessage} from '@/composables/query/mentor/useAssistantSendMessage'
import {useAssistantStore} from '@/stores/assistant'
import {useGlobalStore} from '@/stores/global'
import {useLanguageMapping} from '@/composables/useLanguageMapping'

const THIS_IS_AI_URL: Record<string, string> = {
  de: 'https://knowledgebase.rateboard.io/knowledge-base/ai-assistant',
  en: 'https://knowledgebase.rateboard.io/en/knowledge-base/ai-assistant',
  it: 'https://knowledgebase.rateboard.io/it/knowledge-base/ai-assistant',
}

const props = defineProps<{
  companyId: number
  creditUsage?: CreditUsage
  intent: ConversationIntent
}>()

const emits = defineEmits<{
  pendingMessageResolved: []
}>()

const assistantStore = useAssistantStore()
const globalStore = useGlobalStore()
const {activeConversationId, isVisible} = storeToRefs(assistantStore)

const {sendMessage, isSending, pendingMessages} = useAssistantSendMessage(props.companyId, activeConversationId)
const {messages, hasNextPage, isFetchingNextPage, isLoading, fetchNextPage, updateMessageById} = useAssistantMessages(
  props.companyId,
  activeConversationId,
  {
    enabled: computed(() => Boolean(activeConversationId.value)),
  }
)

async function checkPendingMessage(messageId: number) {
  const message = await updateMessageById(messageId)
  if (message.type === ConversationMessageType.ASSISTANT && message.content.status !== AssistantMessageStatus.PENDING) {
    emits('pendingMessageResolved')
  }
}

const sortedMessages = computed(() => [...messages.value].sort((a, b) => a.id - b.id))
const lastMessage = computed(() => sortedMessages.value.at(-1))
const lastPendingMessageId = computed(() =>
  lastMessage.value?.type === ConversationMessageType.ASSISTANT &&
  lastMessage.value.content.status === AssistantMessageStatus.PENDING
    ? lastMessage.value.id
    : undefined
)

const hasCreditsLeft = computed(
  () =>
    globalStore.hasAuthority('ADMIN') ||
    (!!props.creditUsage && props.creditUsage.isActiveTier && props.creditUsage.availableCredits > 0)
)
const hasPendingAssistantMessage = computed(() => !!lastPendingMessageId.value)

const thisIsAiUrl = useLanguageMapping(THIS_IS_AI_URL)

async function onInput(userPrompt: UserPrompt) {
  const result = await sendMessage(userPrompt)
  const conversation = await props.intent.updateConversationById(result.conversationId)
  assistantStore.setActiveConversation(conversation)
}

async function onSelectSuggestion(suggestion: string) {
  await onInput({prompt: suggestion, preferredFormat: null})
}

async function onSelectPreviousMessage(content: UserMessageContent) {
  await onInput({prompt: content.prompt, preferredFormat: content.preferredFormat ?? null})
}
</script>

<template>
  <div class="w-full flex-1 min-h-0 flex flex-col overflow-hidden">
    <!-- Messages Area -->
    <div class="flex-1 min-h-0 overflow-hidden">
      <div class="w-full h-full min-h-0 mx-auto" :style="{maxWidth: `${CHAT_AREA_MESSAGES_MAX_WIDTH_PX}px`}">
        <NonEmptyState
          v-if="activeConversationId"
          :key="activeConversationId"
          :company-id="companyId"
          :credit-usage="creditUsage"
          :fetch-next-page="fetchNextPage"
          :has-credits-left="hasCreditsLeft"
          :has-next-page="hasNextPage"
          :is-fetching-next-page="isFetchingNextPage"
          :is-loading="isLoading"
          :is-sending="isSending"
          :last-pending-message-id="lastPendingMessageId"
          :messages="sortedMessages"
          :update-message-by-id="checkPendingMessage"
          @select-previous-message="onSelectPreviousMessage"
          @select-suggestion="onSelectSuggestion"
        />
        <EmptyState
          v-else
          :company-id="companyId"
          :credit-usage="creditUsage"
          :has-credits-left="hasCreditsLeft"
          :pending-messages="pendingMessages"
        />
      </div>
    </div>

    <!-- Input Area -->
    <div class="border-t-new-sm shrink-0 print:hidden" style="padding-bottom: env(safe-area-inset-bottom)">
      <div class="w-full mx-auto p-3" :style="{maxWidth: `${CHAT_AREA_INPUT_MAX_WIDTH_PX}px`}">
        <template v-if="hasCreditsLeft">
          <InputArea
            :disabled="isSending || hasPendingAssistantMessage"
            :open-suggestions="!activeConversationId"
            :sending="isSending"
            :visible="isVisible"
            @input="onInput"
          />

          <div class="text-new-neutral-500 text-sm text-center mt-1">
            <a class="text-new-neutral-500 underline" :href="thisIsAiUrl" target="_blank">{{
              $t('assistant.chat.disclaimer.linkText')
            }}</a>
            {{ $t('assistant.chat.disclaimer.text') }}
          </div>
        </template>

        <div v-else class="w-full p-4 flex flex-col gap-2 bg-new-neutral-0 rounded-md">
          <UsageLimitMessage :credit-usage="creditUsage" />
        </div>
      </div>
    </div>
  </div>
</template>
